// HTTP Client Service - Optimized Production-Safe Version
import apiConfig from '../../config/config';

/**
 * Professional HTTP Client with advanced features:
 * - Request validation and normalization
 * - Intelligent retry mechanism with exponential backoff
 * - CORS and security handling
 * - Request deduplication and rate limiting
 * - Comprehensive error handling
 * - Response caching and optimization
 * - Production-safe logging and monitoring
 */
class HttpClient {
  constructor() {
    // Request management
    this.requestQueue = new Map();
    this.activeRequests = new Set();
    this.responseCache = new Map();
    this.rateLimitMap = new Map();
    
    // Statistics
    this.stats = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      cachedResponses: 0,
      averageResponseTime: 0
    };
    
    this.initializeClient();
  }

  //  Üretimde gürültüyü azaltmak için log yardımcıları
  logDebug(...args) {
    if (!this.config?.isProduction) {
      console.log(...args);
    }
  }
  logWarn(...args) {
    if (!this.config?.isProduction) {
      console.warn(...args);
    }
  }
  logError(...args) {
    // Hata loglarını üretimde de göster
    console.error(...args);
  }

  /**
   * Initialize the HTTP client with configuration
   */
  initializeClient() {
    this.config = apiConfig;
    this.features = this.config.getFeatureFlags();
    
    // Setup cleanup intervals
    this.setupCleanupIntervals();
    
    this.logDebug('HttpClient initialized:', {
      features: Object.keys(this.features).filter(key => this.features[key]),
      environment: this.config.isProduction ? 'PRODUCTION' : 'DEVELOPMENT'
    });
  }

  /**
   * Setup periodic cleanup to prevent memory leaks
   */
  setupCleanupIntervals() {
    // Clear expired cache entries every 5 minutes
    setInterval(() => this.cleanupCache(), 5 * 60 * 1000);
    
    // Clear old rate limit entries every minute
    setInterval(() => this.cleanupRateLimit(), 60 * 1000);
    
    // Log statistics every 10 minutes in development
    if (!this.config.isProduction) {
      setInterval(() => this.logStatistics(), 10 * 60 * 1000);
    }
  }

  /**
   * Main HTTP request method with all features
   */
  async customRequest(url, method = 'GET', headers = {}, body = null, options = {}) {
    const requestId = this.generateRequestId(method, url);
    const startTime = performance.now();
    
    try {
      // 1. Update statistics
      this.stats.totalRequests++;
      
      // 2. Validate request
      await this.validateRequest(url, method, headers, body);
      
      // 3. Check cache first (for GET requests)
      if (method.toUpperCase() === 'GET' && this.features.enableResponseCache) {
        const cachedResponse = this.getCachedResponse(url, headers);
        if (cachedResponse) {
          this.stats.cachedResponses++;
          return cachedResponse;
        }
      }
      
      // 4. Normalize URL
      const normalizedURL = this.normalizeURL(url);
      
      // 5. Rate limiting
      await this.handleRateLimit(normalizedURL);
      
      // 6. Deduplication
      const existingRequest = this.checkDuplicateRequest(requestId);
      if (existingRequest) {
        return await existingRequest;
      }
      
      // 7. Build request configuration
      const requestConfig = this.buildRequestConfig(method, headers, body, options);
      
      // 8. Execute request with retry logic
      const requestPromise = this.executeRequestWithRetry(
        normalizedURL, 
        requestConfig, 
        { ...options, method, startTime, requestId }
      );
      
      // 9. Track active request
      this.activeRequests.add(requestId);
      this.requestQueue.set(requestId, requestPromise);
      
      // 10. Execute and get response
      const response = await requestPromise;
      
      // 11. Cache successful GET responses
      if (method.toUpperCase() === 'GET' && response.ok && this.features.enableResponseCache) {
        this.cacheResponse(url, headers, response);
      }
      
      // 12. Update statistics
      this.updateStatistics(response, startTime);
      
      // 13. Cleanup
      this.cleanupRequest(requestId);
      
      // 14. Log request (if enabled)
      this.logRequest(normalizedURL, method, response);
      
      return response;
      
    } catch (error) {
      // Handle errors gracefully
      this.stats.failedRequests++;
      this.cleanupRequest(requestId);
      
      const errorResponse = this.createErrorResponse(error, {
        url, method, startTime, requestId
      });
      
      // In production, return error response instead of throwing
      if (this.config.isProduction) {
        return errorResponse;
      } else {
        throw new HttpClientError(error.message, errorResponse);
      }
    }
  }

  /**
   * Generate unique request ID
   */
  generateRequestId(method, url) {
    return `${method.toUpperCase()}_${this.hashString(url)}_${Date.now()}`;
  }

  /**
   * Simple string hash function
   */
  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Enhanced request validation
   */
  async validateRequest(url, method, headers, body) {
    const validation = this.config.validateRequest(url, method, headers, body);
    if (!validation.isValid) {
      throw new ValidationError(`İstek doğrulama başarısız: ${validation.errors.join(', ')}`);
    }
    
    // Additional custom validations
    if (url.length > 2048) {
      throw new ValidationError('URL çok uzun (en fazla 2048 karakter)');
    }
    
    if (body && typeof body === 'string' && body.length > 10 * 1024 * 1024) {
      throw new ValidationError('İstek gövdesi çok büyük (en fazla 10MB)');
    }
  }

  /**
   * URL normalization with enhanced security
   */
  normalizeURL(url) {
    this.logDebug('normalizeURL input:', url);
  
    try {
      const normalizedURL = this.config.normalizeURL(url);
      this.logDebug('normalizeURL output:', normalizedURL);
      
      if (normalizedURL.startsWith('file://')) {
        throw new SecurityError('Yerel dosya erişimine izin verilmez');
      }
  
      return normalizedURL;
    } catch (error) {
      throw new URLError(`URL normalizasyonu başarısız: ${error.message}`);
    }
  }
  

  /**
   * Intelligent rate limiting
   */
  async handleRateLimit(url) {
    if (!this.features.enableRateLimit) return;
    
    const key = this.hashString(url);
    const now = Date.now();
    const lastRequest = this.rateLimitMap.get(key);
    
    if (lastRequest && (now - lastRequest) < this.config.REQUEST_THROTTLE_MS) {
      const waitTime = this.config.REQUEST_THROTTLE_MS - (now - lastRequest);
      this.logWarn(`Rate limited, waiting ${waitTime}ms for:`, url);
      await this.delay(waitTime);
    }
    
    this.rateLimitMap.set(key, now);
  }

  /**
   * Check for duplicate requests
   */
  checkDuplicateRequest(requestId) {
    // Simple deduplication based on URL hash
    const urlHash = requestId.split('_')[1];
    for (const [activeId, promise] of this.requestQueue) {
      if (activeId.includes(urlHash) && activeId !== requestId) {
        this.logWarn('Duplicate request detected, reusing existing');
        return promise;
      }
    }
    return null;
  }

  /**
   * Build optimized request configuration
   */
  buildRequestConfig(method, headers, body, options) {
    const requestConfig = {
      method: method.toUpperCase(),
      mode: 'cors',
      credentials: options.credentials || 'omit',
      cache: 'no-store',
      redirect: 'follow',
      referrerPolicy: 'no-referrer-when-downgrade',
      ...options
    };

    // Enhanced headers with CORS optimization
    requestConfig.headers = {
      'Accept': 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...this.config.getDefaultHeaders(),
      ...headers
    };

    // Handle request body
    if (body !== null && !['GET', 'HEAD'].includes(method.toUpperCase())) {
      requestConfig.body = this.processRequestBody(body, requestConfig.headers);
    }

    return requestConfig;
  }

  /**
   * Enhanced request body processing
   */
  processRequestBody(body, headers) {
    if (body === null || body === undefined) {
      return null;
    }

    // Handle different body types
    if (typeof body === 'string') {
      return body;
    }

    if (body instanceof FormData) {
      // Remove Content-Type for FormData (browser sets it with boundary)
      delete headers['Content-Type'];
      return body;
    }

    if (body instanceof URLSearchParams || 
        body instanceof Blob ||
        body instanceof ArrayBuffer) {
      return body;
    }

    // JSON objects with validation
    if (typeof body === 'object') {
      try {
        headers['Content-Type'] = 'application/json; charset=utf-8';
        return JSON.stringify(body);
      } catch (error) {
        throw new ValidationError(`Geçersiz JSON gövde: ${error.message}`);
      }
    }

    return String(body);
  }

  /**
   * Execute request with intelligent retry mechanism
   */
  async executeRequestWithRetry(url, requestConfig, options) {
    const { method, startTime, requestId, maxRetries = this.config.MAX_RETRIES } = options;
    let lastError;
    let retryDelay = this.config.RETRY_DELAY;

    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      try {
        if (this.features.enableRequestLogging && attempt > 1) {
          this.logDebug(`Retry attempt ${attempt} for ${method} ${url}`);
        }

        // Per-attempt AbortController + timeout
        const controller = new AbortController();
        const timeoutMs = options.timeout || this.config.TIMEOUT;
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        const attemptConfig = { ...requestConfig, signal: controller.signal };

        const response = await fetch(url, attemptConfig);
        const processedResponse = await this.processResponse(response, startTime);

        // Success - clear timeout
        clearTimeout(timeoutId);

        if (attempt > 1) {
          this.logDebug(`Request succeeded on attempt ${attempt}`);
        }

        return processedResponse;

      } catch (error) {
        lastError = error;
        // Clear timeout on error (safe)
        try { /* no-op: per attempt cleared inline */ } catch {}

        // Determine if we should retry
        const shouldRetry = this.shouldRetryRequest(error, attempt, maxRetries);
        
        if (!shouldRetry) {
          this.logError(`❌ Request failed after ${attempt} attempts:`, error.message);
          throw error;
        }

        // Exponential backoff with jitter
        const jitter = Math.random() * 1000;
        const waitTime = retryDelay * Math.pow(2, attempt - 1) + jitter;
        
        this.logWarn(`Retrying in ${Math.round(waitTime)}ms (${attempt}/${maxRetries})`);
        await this.delay(waitTime);
      }
    }

    throw lastError;
  }

  /**
   * Determine if request should be retried
   */
  shouldRetryRequest(error, attempt, maxRetries) {
    if (attempt > maxRetries) return false;

    // Don't retry client errors (4xx)
    if (error.status >= 400 && error.status < 500) return false;

    // Retry on network errors, timeouts, and server errors (5xx)
    if (error.name === 'AbortError' || 
        error.name === 'TypeError' ||
        error.message.includes('fetch') ||
        (error.status >= 500)) {
      return true;
    }

    return false;
  }

  /**
   * Enhanced response processing
   */
  async processResponse(response, startTime) {
    const endTime = performance.now();
    const responseTime = Math.round(endTime - startTime);

    // Extract headers efficiently
    const headers = Object.fromEntries(response.headers.entries());

    // Determine content type and parse accordingly
    const contentType = response.headers.get('content-type') || '';
    let data = null;

    try {
      if (contentType.includes('application/json')) {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
      } else if (contentType.includes('text/')) {
        data = await response.text();
      } else if (contentType.includes('application/octet-stream') || 
                 contentType.includes('image/') ||
                 contentType.includes('video/') ||
                 contentType.includes('audio/')) {
        data = await response.blob();
      } else {
        // Try JSON first, fallback to text
        const text = await response.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            data = text;
          }
        }
      }
    } catch (parseError) {
      this.logWarn('Response parsing failed:', parseError.message);
      data = null;
    }

    const processedResponse = {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      headers,
      data,
      responseTime,
      url: response.url,
      redirected: response.redirected,
      type: response.type,
      timestamp: new Date().toISOString(),
      size: this.calculateResponseSize(data, headers)
    };

    // Throw error for non-OK responses
    if (!response.ok) {
      const error = new HttpResponseError(
        `HTTP ${response.status}: ${response.statusText}`,
        processedResponse
      );
      error.status = response.status;
      error.response = processedResponse;
      throw error;
    }
    this.logDebug('İşlenen veri:', processedResponse);
    return processedResponse;
  }

  /**
   * Calculate response size for monitoring
   */
  calculateResponseSize(data, headers) {
    const contentLength = headers['content-length'];
    if (contentLength) {
      return parseInt(contentLength, 10);
    }

    // Estimate size if content-length not available
    if (typeof data === 'string') {
      return new Blob([data]).size;
    } else if (data && typeof data === 'object') {
      return new Blob([JSON.stringify(data)]).size;
    }

    return 0;
  }

  /**
   * Enhanced error response creation
   */
  createErrorResponse(error, context) {
    const { url, method, startTime } = context;
    const responseTime = Math.round(performance.now() - startTime);
    const errorInfo = this.config.handleNetworkError(error, context);

    // Enhanced CORS error handling
    if (error.message && (error.message.includes('CORS') || 
        error.message.includes('cross-origin'))) {
      errorInfo.message = 'CORS Hatası: API çapraz kaynak isteklerine izin vermiyor';
      errorInfo.type = 'CORS_ERROR';
      errorInfo.solution = 'Sunucuda CORS\'u etkinleştirin veya proxy kullanın';
    }

    return {
      ok: false,
      status: error.status || 0,
      statusText: error.message || 'Ağ Hatası',
      headers: error.response?.headers || {},
      data: {
        error: errorInfo.message,
        type: errorInfo.type,
        code: error.code || 'UNKNOWN_ERROR',
        details: this.config.isDevelopment ? {
          originalMessage: error.message,
          stack: error.stack,
          url,
          method
        } : undefined,
        timestamp: new Date().toISOString(),
        requestId: context.requestId
      },
      responseTime,
      url,
      error: true,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Response caching for GET requests
   */
  cacheResponse(url, headers, response, ttl = 5 * 60 * 1000) { // 5 minutes default
    if (!this.features.enableResponseCache) return;

    const cacheKey = this.generateCacheKey(url, headers);
    const cacheEntry = {
      response: { ...response },
      timestamp: Date.now(),
      ttl
    };

    this.responseCache.set(cacheKey, cacheEntry);

    // Prevent cache from growing too large
    if (this.responseCache.size > 100) {
      const oldestKey = this.responseCache.keys().next().value;
      this.responseCache.delete(oldestKey);
    }
  }

  /**
   * Get cached response if valid
   */
  getCachedResponse(url, headers) {
    if (!this.features.enableResponseCache) return null;

    const cacheKey = this.generateCacheKey(url, headers);
    const cacheEntry = this.responseCache.get(cacheKey);

    if (!cacheEntry) return null;

    const { response, timestamp, ttl } = cacheEntry;
    const age = Date.now() - timestamp;

    if (age > ttl) {
      this.responseCache.delete(cacheKey);
      return null;
    }

    // Return a copy to prevent mutations
    return {
      ...response,
      cached: true,
      cacheAge: age
    };
  }

  /**
   * Generate cache key from URL and relevant headers
   */
  generateCacheKey(url, headers) {
    const relevantHeaders = ['authorization', 'accept', 'accept-language'];
    const headerString = relevantHeaders
      .map(h => `${h}:${headers[h] || ''}`)
      .join('|');
    
    return this.hashString(`${url}|${headerString}`);
  }

  /**
   * Update request statistics
   */
  updateStatistics(response, startTime) {
    const responseTime = Math.round(performance.now() - startTime);
    
    if (response.ok) {
      this.stats.successfulRequests++;
    } else {
      this.stats.failedRequests++;
    }

    // Update average response time using moving average
    const totalResponses = this.stats.successfulRequests + this.stats.failedRequests;
    this.stats.averageResponseTime = (
      (this.stats.averageResponseTime * (totalResponses - 1) + responseTime) / totalResponses
    );
  }

  /**
   * Cleanup request tracking
   */
  cleanupRequest(requestId) {
    this.activeRequests.delete(requestId);
    this.requestQueue.delete(requestId);
  }

  /**
   * Cleanup expired cache entries
   */
  cleanupCache() {
    const now = Date.now();
    for (const [key, entry] of this.responseCache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.responseCache.delete(key);
      }
    }
  }

  /**
   * Cleanup old rate limit entries
   */
  cleanupRateLimit() {
    const cutoff = Date.now() - (5 * 60 * 1000); // 5 minutes
    for (const [key, timestamp] of this.rateLimitMap.entries()) {
      if (timestamp < cutoff) {
        this.rateLimitMap.delete(key);
      }
    }
  }

  /**
   * Enhanced logging
   */
  logRequest(url, method, response) {
    if (!this.features.enableRequestLogging) return;

    const status = response.status;
    const time = response.responseTime;
    const size = response.size;
    
    const logData = {
      timestamp: new Date().toISOString(),
      method,
      url,
      status,
      responseTime: time,
      responseSize: size,
      cached: response.cached || false,
      success: response.ok
    };

    if (response.ok) {
      console.log(
        `${method} ${status} (${time}ms, ${this.formatBytes(size)})${response.cached ? ' [CACHED]' : ''}: ${url}`
      );
    } else {
      console.error(
        `${method} ${status} (${time}ms): ${url}`
      );
    }

    // Send to external logging service in production
    if (this.config.isProduction && this.features.enableErrorReporting) {
      this.sendToLoggingService(logData);
    }
  }

  /**
   * Log statistics summary
   */
  logStatistics() {
    this.logDebug('HTTP Client Statistics:', {
      ...this.stats,
      successRate: this.stats.totalRequests > 0 
        ? Math.round((this.stats.successfulRequests / this.stats.totalRequests) * 100) + '%'
        : '0%',
      cacheHitRate: this.stats.totalRequests > 0
        ? Math.round((this.stats.cachedResponses / this.stats.totalRequests) * 100) + '%'
        : '0%',
      activeRequests: this.activeRequests.size,
      cacheSize: this.responseCache.size
    });
  }

  /**
   * Format bytes for human readability
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Send logs to external service (placeholder)
   */
  sendToLoggingService(logData) {
    // Implement your logging service integration here
    // e.g., send to Sentry, LogRocket, or custom analytics
  }

  /**
   * Utility: Delay function
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Convenience HTTP methods
   */
  async get(url, headers = {}, options = {}) {
    return this.customRequest(url, 'GET', headers, null, options);
  }

  async post(url, body, headers = {}, options = {}) {
    return this.customRequest(url, 'POST', headers, body, options);
  }

  async put(url, body, headers = {}, options = {}) {
    return this.customRequest(url, 'PUT', headers, body, options);
  }

  async patch(url, body, headers = {}, options = {}) {
    return this.customRequest(url, 'PATCH', headers, body, options);
  }

  async delete(url, headers = {}, options = {}) {
    return this.customRequest(url, 'DELETE', headers, null, options);
  }

  async head(url, headers = {}, options = {}) {
    return this.customRequest(url, 'HEAD', headers, null, options);
  }

  async options(url, headers = {}, options = {}) {
    return this.customRequest(url, 'OPTIONS', headers, null, options);
  }

  /**
   * Health check with timeout
   */
  async healthCheck(endpoint = '/health', timeout = 5000) {
    try {
      const response = await this.get(endpoint, {}, { timeout });
      return {
        healthy: response.ok,
        status: response.status,
        responseTime: response.responseTime
      };
    } catch (error) {
      return {
        healthy: false,
        error: error.message,
        responseTime: 0
      };
    }
  }

  /**
   * Get comprehensive client status
   */
  getStatus() {
    return {
      statistics: this.stats,
      activeRequests: this.activeRequests.size,
      queuedRequests: this.requestQueue.size,
      cacheSize: this.responseCache.size,
      rateLimitEntries: this.rateLimitMap.size,
      config: this.config.getEnvironmentInfo(),
      features: this.features,
      memoryUsage: this.getMemoryUsage()
    };
  }

  /**
   * Get memory usage information
   */
  getMemoryUsage() {
    if (typeof performance !== 'undefined' && performance.memory) {
      return {
        used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024) + ' MB',
        total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024) + ' MB',
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024) + ' MB'
      };
    }
    return null;
  }

  /**
   * Clear all caches and reset statistics
   */
  reset() {
    this.responseCache.clear();
    this.rateLimitMap.clear();
    this.activeRequests.clear();
    this.requestQueue.clear();
    
    this.stats = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      cachedResponses: 0,
      averageResponseTime: 0
    };
    
    this.logDebug('HTTP Client reset completed');
  }

  /**
   * Graceful shutdown
   */
  async shutdown() {
    this.logDebug('Shutting down HTTP Client...');
    
    // Wait for active requests to complete (with timeout)
    const activePromises = Array.from(this.requestQueue.values());
    if (activePromises.length > 0) {
      this.logDebug(`⏳ Waiting for ${activePromises.length} active requests...`);
      try {
        await Promise.race([
          Promise.allSettled(activePromises),
          this.delay(10000) // 10 second timeout
        ]);
      } catch (error) {
        this.logWarn('Some requests did not complete during shutdown');
      }
    }
    
    this.reset();
    this.logDebug('✅ HTTP Client shutdown completed');
  }
}

/**
 * Custom Error Classes
 */
class HttpClientError extends Error {
  constructor(message, response) {
    super(message);
    this.name = 'HttpClientError';
    this.response = response;
  }
}

class ValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ValidationError';
  }
}

class URLError extends Error {
  constructor(message) {
    super(message);
    this.name = 'URLError';
  }
}

class SecurityError extends Error {
  constructor(message) {
    super(message);
    this.name = 'SecurityError';
  }
}

class HttpResponseError extends Error {
  constructor(message, response) {
    super(message);
    this.name = 'HttpResponseError';
    this.response = response;
  }
}

// Create and export singleton instance
const httpClient = new HttpClient();

// Handle page unload gracefully
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    httpClient.shutdown();
  });
}

export default httpClient;
export { HttpClientError, ValidationError, URLError, SecurityError, HttpResponseError };
